<template>
  <v-container>

    <loading v-model="loading" />

    <v-snackbar v-model="msg.show" value="true" right top multi-line :color="msg.type" :timeout="msg.time">
      {{ msg.text }}
      <v-btn dark flat @click="msg.show = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-card fluid>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>{{ $t('Orders') }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-tooltip bottom><!-- TODO: extract to component -->
          <v-menu slot="activator"
            :close-on-content-click="false"
            :nudge-width="200"
            offset-x
            left
          >
            <v-btn icon slot="activator">
              <v-icon v-if="headers.some(header => !header.show)" class="text--secondary">settings_suggest</v-icon>
              <v-icon v-else class="text--secondary">settings</v-icon>
            </v-btn>

            <v-card>
              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
              <v-divider></v-divider>
              <v-list dense>
                <v-list-tile v-for="(header,key) in headers.filter(header => header.value !== 'actions')" :key="'col-'+key" @click.prevent="columnToggle(header)">
                  <v-list-tile-action>
                    <v-switch :value="header.show" :input-value="header.show" color="primary" :disabled="columnHideCheck(header)"></v-switch>
                  </v-list-tile-action>
                  <v-list-tile-title>{{ header.text }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-card>
          </v-menu>
          <span>{{ $t('Table settings') }}</span>
        </v-tooltip>
      </v-toolbar>

      <v-data-table
        :headers="headersShown"
        :items="items"
        :pagination.sync="pagination"
        :total-items="totalItems"
        item-key="id"
        :loading="loading"
        :rows-per-page-items="[5, 10, 25, { text: $t('All'), value: -1 }]"
        :rows-per-page-text="$t('Rows per page')"
        :no-data-text="$t('No data')"
        :no-results-text="$t('No results')"
        class="elevation-1"
      >
        <template v-slot:progress>
          <v-progress-linear indeterminate height="2" style="position: absolute; width: 100%; z-index: 10;"></v-progress-linear>
        </template>
        <template v-slot:headers="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="[
                'column',
                header.sortable ? 'sortable' : '',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : '',
                'col-' + header.value]"
              class="px-2"
            >
              <template v-if="header.nofilter">
                <div @click="changeSort(header)" v-if="header.sortable">
                  <v-icon small>arrow_upward</v-icon>
                  {{ header.text }}
                </div>
                <div v-else>
                  {{ header.text }}
                </div>
              </template>
              <template v-else-if="header.value === 'order_date'">
                <v-input-date-range-picker v-model="header.filter" @input="getItems" @sort="changeSortEmited($event, header)"></v-input-date-range-picker>
              </template>
              <template v-else>
                <v-text-field
                  :label="header.text"
                  v-model="header.filter"
                  @keyup.enter="getItems()"

                  append-icon="search"
                  @click:append="getItems()"

                  clearable
                  @click:clear="header.filter = null; getItems()"

                  prepend-inner-icon="arrow_upward"
                  @click:prepend-inner="changeSort(header)"

                  hide-details
                  full-width background-color="grey lighten-4"
                ></v-text-field>
              </template>
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <tr
            :active="props.expanded"
            @click="props.item.consumables.length ? props.expanded = !props.expanded : null"
            :style="props.item.consumables.length === 0 ? 'opacity: 0.3' : ''"
          >
            <td
              v-for="header in headersShown"
              :key="header.value"
              :class="['text-xs-' + header.align, header.value === 'actions' ? 'px-1' : '']"
            >

              <template v-if="header.value === 'order_date'">
                {{ printDateTime(props.item.order_date) }}
              </template>

              <template v-else-if="header.value === 'completion_date'">
                <span v-if="props.item.completion_date">
                  {{ printDateTime(props.item.completion_date) }}
                </span>
                <v-chip v-else small label dark color="red darken-2">
                  <span>{{ $t('Incompleted') }}</span>
                </v-chip>

                <!-- <span :class="props.item.target ? 'green--text' : ''">{{ props.item.order_code }}</span> -->
              </template>

              <template v-else-if="header.value === 'product_count'">
                {{ props.item.consumables.length }}
              </template>

              <template v-else-if="header.value === 'address'">
                <v-icon small class="mr-2">home</v-icon>
                <span class="grey--text text--darken-1 font-weight-light">{{ addressString(props.item.target.address).address }}</span>
              </template>

              <template v-else-if="header.value === 'actions'">
                <v-tooltip bottom>
                  <v-btn slot="activator" icon flat @click.stop="cancel(props.item)" color="error" class="ma-0">
                    <v-icon>cancel</v-icon>
                  </v-btn>
                  <span>{{ $t('Cancel delivery note') }}</span>
                </v-tooltip>
              </template>

              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
          </tr>
        </template>
        <template v-slot:expand="props">

          <v-card flat class="grey lighten-4" style="border-top: solid white 1px !important">

            <v-layout fill-height align-center>
              <v-flex class="xs12 sm8 py-2 pr-4" style="border-right: solid white 1px">
                <v-layout v-for="(consumable, index) in props.item.consumables" :key="consumable.id" class="py-1">
                  <v-flex class="xs1 pr-4 text-xs-right">
                    <span class="grey--text text--darken-1 font-weight-light">{{ index+1 }}</span>
                  </v-flex>
                  <v-flex>
                    <span class="grey--text text--darken-1 font-weight-light mr-1">{{ consumable.id }}</span>
                    {{ consumable.name }}
                  </v-flex>
                  <v-flex class="xs2 text-xs-right">
                    {{ showAmount(consumable.price) }}
                    <span v-if="!!consumable.alt_price" class="grey--text text--darken-1 font-weight-light ml-1">{{ showAmount(consumable.alt_price) }}</span>
                  </v-flex>
                  <v-flex class="xs1 text-xs-right">
                    {{ consumable.quantity }}
                    <span class="grey--text text--darken-1 font-weight-light ml-1">{{ $t('pcs') }}</span>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex class="xs12 sm4 pa-2 pl-3" v-if="props.item.target">
                <template>
                  <span class="grey--text text--darken-1 font-weight-light mr-2">{{ $t('Receiver') }}</span>
                  <span class="mr-2">{{ props.item.target.partner.name }}</span>
                  <span class="grey--text mr-2">&bull;</span>
                  <span class="grey--text text--darken-1 font-weight-light mr-2">{{ props.item.target.contact_person }}</span>
                  <span class="grey--text mr-2" v-if="props.item.target.contact_person">&bull;</span>
                  {{ addressString(props.item.target.address).address }}
                </template>
              </v-flex>
            </v-layout>

          </v-card>

        </template>
        <template v-slot:footer>
          <td :colspan="headersShown.length" class="text-xs-right">

            {{ $t('Orders total') }}:
            <span class="font-weight-bold ml-2">{{ totalItems }}</span>

          </td>
        </template>
        <template v-slot:actions-prepend>
          <v-pagination v-model="pagination.page" circle :length="pages" total-visible="7" class="pagination"></v-pagination>
        </template>
      </v-data-table>

    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions }            from 'vuex';
import { consumables }                       from '@/resources';
import { addressString, printDateTime, msg, floatTrim, showAmount } from '@/components/util';
import Loading                               from '@/components/Loading.vue';
import VInputDateRangePicker                 from '@/components/VInputDateRangePicker';

export default {
  name: 'consumables-orders',
  components: {
    Loading,
    VInputDateRangePicker
  },
  data () {
    return {
      loading: false,

      headers: [
        { text: this.$t('Id'),             value: 'id',               align: 'left',   sortable: true,  show: true,  nofilter: true },
        { text: this.$t('Order date'),     value: 'order_date',       align: 'left',   sortable: true,  show: true,  filter: { start_date: undefined, end_date: undefined } },
        { text: this.$t('Date completed'), value: 'completion_date',  align: 'left',   sortable: true,  show: true,  nofilter: true },
        { text: this.$t('Cons. cnt'),      value: 'product_count',    align: 'right',  sortable: false, show: true,  nofilter: true },

        { text: this.$t('Target Address'), value: 'address',          align: 'left',   sortable: true,  show: false, nofilter: true },
        // { text: this.$t('Actions'),        value: 'actions',          align: 'center', sortable: false, show: true,  nofilter: true, persist: true },
      ],
      items: [],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'date',
        descending: true
      },

      msg,
      addressString,
      printDateTime,
      floatTrim,
      showAmount
    }
  },
  computed: {
    ...mapGetters(['consumablesOrders']),

    pages() {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;

      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    },

    headersShown() {
      return this.headers.filter(elem => !!elem.show);
    },

    filtersOn() {
      let filters = {};
      this.headers.forEach(elem => {
        if (!!elem.filter) {
          let key = elem.value;
          if (elem.value === 'order_date') {

            if (elem.filter['start_date']) {
              filters['order_date_start'] = elem.filter['start_date'];
            }
            if (elem.filter['end_date']) {
              filters['order_date_end'] = elem.filter['end_date'];
            }

          }
          else
          if (elem.value === 'date') {
            ['start_date', 'end_date'].forEach(key2 => {

              if (elem.filter[key2]) {
                filters[key2] = elem.filter[key2];
              }

            });
          }
          else {
            filters[key] = elem.filter;
          }
        }
      });
      return filters;
    }
  },
  methods: {
    ...mapActions(['updateConsumablesOrders']),

    getItems(isPaginate) {
      if (this.loading) {
        return;
      }
      this.loading = true;

      this.storeFilter();

      let params = this.filtersOn;
      let { sortBy, descending, page, rowsPerPage } = this.pagination;
      if (!isPaginate) {
        page = 1;
      }

      let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
      let length = (rowsPerPage>=1) ? rowsPerPage : -1;
      let sort   = (sortBy) ? (descending ? '-' : '') + sortBy : '';
      if (sort)
        params = Object.assign({ length, offset, sort }, params);
      else
        params = Object.assign({ length, offset }, params);

      consumables.listOrders(params).then(response => {

        this.totalItems = response.body.length;
        this.items      = response.body.result;

      }).catch(response => {

        if (response.body.errors) {
          this.msg.popup(response.body.errors.map(elem => elem.text).join(', '), 'error');
        }
        else {
          this.msg.popup((response.status === 403 ? this.$t('Access denied') : 'Error loading orders list: ' + response.statusText), 'error');
        }

      }).finally(() => {
        this.loading = false;
      });
    },

    columnHideCheck(header) {
      if (header.persist)
        return true;

      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },
    columnToggle(header) {
      if (!header.persist) {
        header.show = !header.show;
        this.storeFilter();
      }
    },

    changeSort(header) {
      if (!header.sortable)
        return;
      let column = header.value;
      if (this.pagination.sortBy === column) {
        if (this.pagination.descending)
          this.pagination.sortBy = null;
        else
          this.pagination.descending = true;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    changeSortEmited(evt, header) {
      this.changeSort(header);
    },

    storeFilter() {
      let s = {
        pagination:  this.pagination,
        headersdata: this.headers.filter(header => header.value !== 'actions')
          .map(header => {
            return {
              show: !!header.show,
              filter: header.filter
            }
          }),
      };
      this.updateConsumablesOrders(JSON.stringify(s));
    },

  },
  watch: {
    pagination: {
      handler() {
        this.getItems('isPaginate');
      },
      deep: true
    },
    'calendar.popup'(val) {
      if (this.calendar.popup) {
        this.calendar.dates = [];
        const header = this.headers.find(elem => elem.value === 'date');
        if (header.filter.start_date) {
          this.calendar.dates.push( header.filter.start_date );
        }
        if (header.filter.end_date) {
          this.calendar.dates.push( header.filter.end_date );
        }
      }
    }
  },
  mounted() {
    {// reStore
      let storedFilter = null;
      try {
        storedFilter = JSON.parse(this.consumablesOrders);
      } catch(e) {
        console.log('Filter cannot be parsed', e);
        storedFilter = null;
      }

      if (storedFilter) {
        storedFilter.headersdata.forEach((elem, idx) => {
          if (typeof elem.show !== 'undefined') {
            this.headers[idx].show = elem.show;
          }
          if (typeof elem.filter !== 'undefined') {
            this.headers[idx].filter = elem.filter;
          }
        });

        if (storedFilter.pagination) {
          this.pagination.page        = storedFilter.pagination.page;
          this.pagination.rowsPerPage = storedFilter.pagination.rowsPerPage;
          this.pagination.totalItems  = storedFilter.pagination.totalItems;
        }
      }
    }


    // MOCK
    // this.totalItems = RESPONSE.length;
    // this.items      = RESPONSE.result;
  }
}

// MOCK
const RESPONSE = { "offset": 0, "length": 3, "result": [ {"id": 123, "order_date": 1707480316865, "completion_date": 1707489618659, "consumables":[{"id":633113684,"quantity":1,name:"Тест"},{"id":633113685,"quantity":2,name:"Тест2"}], "target":{"address":{"post_code":"1000","settlement":{"code":203,"name":"София","schedule":{"cargo":[true,true,true,true,true,true,false],"courier":[true,true,true,true,true,true,false]}},"country":{"code":"BG","name":"България"},"complex":{"name":""},"street":{"name":"ул. Овчо поле, кв. Суходол"},"street_number":"14","block":null,"entrance":null,"floor":null,"apartment":null,"additional":null},"address_info":{"type":"connection","code":633107551},"partner":{"code":1235733,"name":"\"72-РО ОСНОВНО УЧИЛИЩЕ \"\"ХРИСТО БОТЕВ\"\"\""},"contact_person":"ЕВЕЛИНА ИВАНОВА","contact_phone":"029295350","contact_email":"","instructions":"text 2024-02-08 1805 "} } ] };

</script>

<style scoped>
.input-right >>> input {
  text-align: right;
}
.pagination >>> button {
  box-shadow: none;
}

.expanded-card {
  border-top: 1px solid white;
}

.cursor-link {
  cursor: pointer;
}

.col-status_id {
  width: 10rem;
}
</style>